import { Mail, MessageCircle, Clock } from "lucide-react";

const SECTIONS = [
  {
    n: 1, t: "Order Cancellation",
    blocks: [
      { h: "Before Dispatch", p: "Customers may cancel an order before it has been packed or shipped. If cancelled successfully, a full refund (if prepaid) will be processed to the original payment method within 5 to 7 business days." },
      { h: "After Dispatch", p: "Orders cannot be cancelled once they have been shipped." },
    ],
  },
  {
    n: 2, t: "Return Eligibility",
    p: "Returns are accepted only under the following circumstances:",
    list: [
      "Product received is damaged.",
      "Product received is defective.",
      "Wrong product delivered.",
      "Incorrect size or colour received (different from the order placed).",
      "Product is missing parts or accessories.",
    ],
    sub: "To be eligible for a return:",
    subList: [
      "The return request must be raised within 5 days of delivery.",
      "The product must be unused, unworn, unwashed, and in its original condition.",
      "Original tags, labels, packaging, invoices, and accessories must be intact.",
      "The product must not show signs of use, alteration, or damage caused by the customer.",
    ],
  },
  {
    n: 3, t: "Non-Returnable Products",
    p: "The following products are not eligible for return unless received damaged or incorrect:",
    list: [
      "Products purchased during flash sales or clearance sales.",
      "Personalised or customised products.",
      "Innerwear and hygiene-related products.",
      "Beauty and cosmetic products once opened.",
      "Gift cards or promotional vouchers.",
      'Products specifically marked as "Non-Returnable" on the product page.',
    ],
  },
  {
    n: 4, t: "Return Process",
    p: "To initiate a return:",
    listOrdered: [
      "Log in to your CityShor Pop-Up Bazaar account.",
      "Go to My Orders.",
      "Select the order and click Request Return.",
      "Upload clear photographs or videos showing the issue.",
      "Our support team will review the request within 2 business days.",
    ],
    footer: "If approved, a pickup will be scheduled wherever service is available. In locations where reverse pickup is unavailable, customers may be requested to self-ship the product. Eligible courier charges will be reimbursed.",
  },
  {
    n: 5, t: "Refund Policy",
    p: "Once the returned product is received and passes quality inspection:",
    list: [
      "Refunds for prepaid orders will be credited to the original payment method within 5 to 7 business days.",
      "For Cash on Delivery (COD) orders, refunds will be processed to the customer's registered bank account or UPI ID within 5 to 7 business days after approval.",
      "Shipping charges, gift wrapping charges, and convenience fees (if any) are non-refundable unless the return is due to an error by CityShor Pop-Up Bazaar or the exhibitor.",
      "If a customer requests a return or exchange for reasons other than seller error (damage, defect, wrong item, wrong size, missing accessories), the return shipping charges shall be borne by the customer. Returns without applicable shipping charges paid will not be accepted.",
    ],
  },
  {
    n: 6, t: "Replacement Policy",
    list: [
      "Where stock is available, customers may choose a replacement instead of a refund for eligible products.",
      "If a replacement is unavailable, a full refund will be issued.",
    ],
  },
  {
    n: 7, t: "Damaged or Incorrect Deliveries",
    p: "If you receive a damaged, defective, or incorrect product:",
    list: [
      "Do not remove the tags.",
      "Take clear photos and an unboxing video immediately after opening the package.",
      "Report the issue within 48 hours of delivery.",
    ],
    footer: "Failure to provide sufficient evidence may result in rejection of the claim.",
  },
  {
    n: 8, t: "Exhibitor Responsibility",
    paragraphs: [
      "Products listed on CityShor Pop-Up Bazaar are sold by independent exhibitors. While we facilitate the transaction, each exhibitor is responsible for the quality, accuracy, authenticity, pricing, and fulfilment of their products.",
      "CityShor Pop-Up Bazaar reserves the right to approve or reject return and refund requests based on the evidence provided and our verification process.",
    ],
  },
  {
    n: 9, t: "Return Pickup",
    list: [
      "Reverse pickup services are subject to courier availability based on the customer's PIN code.",
      "If reverse pickup is unavailable, customers may be required to send the product to the designated return address provided by our support team.",
    ],
  },
  {
    n: 10, t: "Refusal of Returns",
    p: "Returns may be declined if:",
    list: [
      "The product has been used, washed, or altered.",
      "Original tags or packaging are missing.",
      "The return request is raised after the specified return period.",
      "The damage is due to misuse or improper handling by the customer.",
      "The returned product does not match the originally shipped item.",
    ],
  },
];

export default function ReturnPolicy() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-14" data-testid="return-policy-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#F48E2E] font-bold mb-2">Support</div>
      <h1 className="font-display text-4xl md:text-5xl mb-3">Return, Refund & Cancellation Policy</h1>
      <p className="text-sm text-gray-500 mb-6">Effective Date: from the purchase date</p>
      <p className="text-gray-700 leading-relaxed mb-10">
        Welcome to CityShor Pop-Up Bazaar. We are committed to providing a seamless shopping experience while supporting independent exhibitors across India. Please read our Return, Refund & Cancellation Policy carefully before placing an order.
      </p>

      <div className="space-y-6">
        {SECTIONS.map((s) => (
          <section key={s.n} data-testid={`policy-section-${s.n}`} className="p-6 bg-white rounded-2xl border border-gray-200">
            <div className="flex items-baseline gap-3 mb-3">
              <div className="font-display text-2xl text-[#F48E2E]">{s.n}.</div>
              <h2 className="font-display text-2xl">{s.t}</h2>
            </div>

            {s.blocks && (
              <div className="space-y-3">
                {s.blocks.map((b, i) => (
                  <div key={i}>
                    <div className="text-sm font-semibold text-gray-900 mb-1">{b.h}</div>
                    <p className="text-gray-700 leading-relaxed">{b.p}</p>
                  </div>
                ))}
              </div>
            )}

            {s.paragraphs && s.paragraphs.map((p, i) => (
              <p key={i} className="text-gray-700 leading-relaxed mb-3 last:mb-0">{p}</p>
            ))}

            {s.p && <p className="text-gray-700 leading-relaxed mb-3">{s.p}</p>}

            {s.list && (
              <ul className="space-y-2 mb-2">
                {s.list.map((li, i) => (
                  <li key={i} className="flex gap-3 text-gray-700 leading-relaxed">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#F48E2E] flex-shrink-0" />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
            )}

            {s.sub && <p className="text-gray-700 leading-relaxed mt-4 mb-2 font-medium">{s.sub}</p>}
            {s.subList && (
              <ul className="space-y-2">
                {s.subList.map((li, i) => (
                  <li key={i} className="flex gap-3 text-gray-700 leading-relaxed">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#F48E2E] flex-shrink-0" />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
            )}

            {s.listOrdered && (
              <ol className="space-y-2 mb-2 list-decimal list-inside marker:text-[#F48E2E] marker:font-semibold">
                {s.listOrdered.map((li, i) => (
                  <li key={i} className="text-gray-700 leading-relaxed pl-1">{li}</li>
                ))}
              </ol>
            )}

            {s.footer && <p className="text-gray-700 leading-relaxed mt-3">{s.footer}</p>}
          </section>
        ))}

        {/* Section 11 - Contact */}
        <section data-testid="policy-section-11" className="p-6 md:p-8 rounded-2xl bg-gradient-to-br from-[#F48E2E] to-[#111827] text-white">
          <div className="flex items-baseline gap-3 mb-4">
            <div className="font-display text-2xl text-white/80">11.</div>
            <h2 className="font-display text-2xl">Contact Us</h2>
          </div>
          <p className="text-white/95 mb-5">For any questions regarding returns, refunds, or cancellations, please contact us:</p>
          <div className="text-sm font-semibold uppercase tracking-widest text-white/80 mb-3">Customer Support</div>
          <div className="space-y-3">
            <a href="mailto:Hi@cityShor.com" className="flex items-center gap-3 text-white hover:underline" data-testid="policy-email">
              <Mail className="h-4 w-4" /> Hi@cityShor.com
            </a>
            <a href="https://wa.me/91" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white hover:underline" data-testid="policy-whatsapp">
              <MessageCircle className="h-4 w-4" /> WhatsApp: +91-
            </a>
            <div className="flex items-center gap-3 text-white">
              <Clock className="h-4 w-4" /> Support Hours: Monday to Saturday, 11:00 AM to 7:00 PM (IST)
            </div>
          </div>
          <p className="text-white/90 mt-5 text-sm">We aim to respond to all support requests within 24 business hours.</p>
        </section>
      </div>
    </div>
  );
}
